<script setup lang="ts">
/**
 * "Ask someone to pay" — the learner's side of §9.5 (`rl-757.27`).
 *
 * **This is the primary conversion path, not an edge case.** §12.1 puts the
 * self-purchase threshold at 16 and the product is for 14-16s (PRD §8.3),
 * so most of the people this is built for cannot buy a seat at all.
 * `POST /subscribe` refuses them with `age_below_purchase_threshold`, and
 * until this component existed that refusal pointed at nothing — the dead
 * end `errors.py` forbids. Everything here is downstream of that: it is
 * offered where the refusal lands, and it is the whole route to a seat.
 *
 * **The copy-link is the guaranteed path, not a fallback.** `share_url`
 * comes back whatever `delivery` says, and this shows it on both branches.
 * §9.8's rule for invitations is that a refused send degrades a
 * notification rather than failing the request; here it is stronger than
 * that, because a 14-year-old holding out a phone to a parent in the
 * kitchen is the LIKELY happy path and a mailbox is the slow one. Hiding
 * the link behind an error state would hide the thing that works.
 *
 * **The learner is never told anybody said no.** §9.5: a declined request
 * reads as "not funded yet" plus when they may ask again. The recipient is
 * usually a parent or carer, the reader is a child, and "Pat declined" is
 * a sentence about a relationship that we have no business narrating. The
 * server projects the state before it answers (see `FundingRequestState`)
 * so the raw word never reaches this file; the copy below has to hold up
 * the same rule in the sentences it writes around it.
 *
 * **Nothing billable exists yet** (§7.3). Asking creates no seat, no
 * agreement and no Stripe object. Nothing here may promise otherwise: the
 * learner has sent a request, not acquired anything.
 */
import { onMounted, ref } from "vue";

import { apiClient, ApiError } from "@/api/client";
import type { FundingRequestCreated, MyFundingRequest } from "@/api/types";
import { renewalLabel } from "@/lib/gettingStarted";

/** The learner's own asks, newest first, in the neutral projection. */
const asks = ref<MyFundingRequest[]>([]);
const payerEmail = ref("");
const submitting = ref(false);
const error = ref<string | null>(null);
/** Set once an ask succeeds; carries the one and only copy of the link. */
const issued = ref<FundingRequestCreated | null>(null);
/** The address that ask went to, kept beside it so the copy can name it. */
const issuedTo = ref("");
const copied = ref(false);
/** The id currently being withdrawn, so only its own control disables. */
const withdrawing = ref<string | null>(null);
/** §11.3.5 — a withdrawal says what it did, including when it did nothing. */
const notice = ref<string | null>(null);

/**
 * Enough to catch a typo before it becomes a 422, and no more — the same
 * check `SeatPurchase` and `FundedSeats` make. The address is proved by the
 * mail and by whoever signs in holding the link, not by a regex here.
 */
function looksLikeEmail(value: string): boolean {
  const at = value.indexOf("@");
  return at > 0 && at === value.lastIndexOf("@") && at < value.length - 1;
}

/**
 * The list is CONTEXT, never a gate. A learner who cannot see their past
 * asks must still be able to make one, so a failure here is swallowed
 * rather than rendered: the alternative is a surface that shows an error
 * and withholds the only route to a seat this account has.
 */
async function load(): Promise<void> {
  try {
    asks.value = await apiClient.listMyFundingRequests();
  } catch {
    asks.value = [];
  }
}

onMounted(load);

/**
 * The refusals this route answers with.
 *
 * Every refusal here carries a plain `detail` sentence rather than a §10
 * reason code — the server's choice, and a considered one: the cooldown
 * window is tunable and a §10 code is permanent, and the nearest existing
 * code ships an action pointing a 14-year-old at the payer's
 * seat-management surface. Even the 422 for an address that cannot be a
 * mailbox is raised by hand rather than left to a validator, precisely so
 * this never has to print an array of pydantic error objects at a child.
 *
 * So the sentence arrives on `ApiError.code`, as every 429's does, and this
 * RENDERS it rather than switching on it. Inventing our own wording would
 * mean maintaining a second copy of the server's cooldown arithmetic, which
 * is `rl-757.53` all over again.
 */
function refusalFor(e: unknown): string {
  if (!(e instanceof ApiError)) {
    return "We could not reach RevisionLab just now. Check your connection and try again.";
  }
  if ((e.status === 409 || e.status === 422 || e.status === 429) && e.code) {
    return e.code;
  }
  if (e.status === 403) {
    // The server recomputes §12.1's threshold on every attempt, so this is
    // the account that turned 16 while the page was open. Not a fault, and
    // the route it names is the one that just became available.
    return "You can buy a seat for yourself now, so there is nobody to ask. Reload the page and the option will be there.";
  }
  return "We could not send that just now. Try again in a moment.";
}

async function ask(): Promise<void> {
  if (submitting.value) return;
  error.value = null;
  notice.value = null;
  const email = payerEmail.value.trim();
  if (!looksLikeEmail(email)) {
    error.value =
      "Enter the email address of the person you want to ask — a parent, carer or guardian.";
    return;
  }
  submitting.value = true;
  try {
    const created = await apiClient.askForFunding(email);
    issued.value = created;
    issuedTo.value = email;
    payerEmail.value = "";
    copied.value = false;
    await load();
  } catch (e) {
    error.value = refusalFor(e);
  } finally {
    submitting.value = false;
  }
}

/**
 * §11.3.5 forbids the silent no-op, so all three outcomes get a sentence.
 *
 * `nothing_to_withdraw` is also what another learner's id and a uuid that
 * never existed answer with — identically — so its sentence says only that
 * nothing changed. Turning it into "no such request" would rebuild the id
 * oracle the server declined to be.
 */
const WITHDRAW_NOTICES: Record<string, string> = {
  withdrawn:
    "That ask has been taken back. The link stops working, and you can ask that address again whenever you like.",
  already_withdrawn: "You had already taken that one back, so nothing changed.",
  nothing_to_withdraw: "There was nothing to take back.",
};

async function withdraw(id: string): Promise<void> {
  if (withdrawing.value !== null) return;
  withdrawing.value = id;
  error.value = null;
  notice.value = null;
  try {
    const result = await apiClient.withdrawFundingRequest(id);
    notice.value =
      WITHDRAW_NOTICES[result.outcome] ?? "That ask is no longer live.";
    // The link we may still be showing belongs to whichever ask was made
    // last, and it is dead if that is the one just withdrawn.
    if (issued.value?.id === id) issued.value = null;
    await load();
  } catch (e) {
    error.value = refusalFor(e);
  } finally {
    withdrawing.value = null;
  }
}

async function copyLink(): Promise<void> {
  const url = issued.value?.share_url;
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    copied.value = true;
  } catch {
    // Clipboard permission can be refused, and the link is on screen in a
    // selectable field regardless — so this is not worth an alert.
    copied.value = false;
  }
}

/**
 * §5's `d MMMM`, so no surface here invents its own date format. Aliased
 * because the template and four branches below read better with it, and
 * because `renewalLabel` is named for the subscription and these are link
 * and cooldown dates.
 */
const on = renewalLabel;

/**
 * What one ask says to the learner who made it.
 *
 * READ `not_funded` FIRST IF YOU ARE CHANGING THIS. It covers a decline,
 * and §9.5 is explicit that the learner sees "not funded yet" and a date
 * they may ask again — never a verdict, never who chose it, never a word
 * that would be uncomfortable to read about a parent. The date is framed as
 * a permission ("you can ask again from…") rather than a penalty, because
 * that is what it is: the cooldown protects the address from being asked
 * repeatedly, and it says nothing about the answer.
 *
 * `lapsed` gets a different sentence for a true reason — nobody opened it —
 * and not as a softer synonym. A learner deciding whether to nudge someone
 * or try a different address needs to know which of the two happened.
 */
function askLine(request: MyFundingRequest): string {
  switch (request.state) {
    case "awaiting": {
      const until = on(request.expires_at);
      return until
        ? `Waiting to hear back · the link works until ${until}`
        : "Waiting to hear back";
    }
    case "funded":
      return "Paid for — your seat is ready.";
    case "not_funded": {
      const again = request.can_ask_again_at;
      if (again !== null && new Date(again).getTime() > Date.now()) {
        return `Not funded yet. You can ask this address again from ${on(again)}, or ask someone else now.`;
      }
      return "Not funded yet. You can ask this address again, or ask someone else.";
    }
    case "lapsed": {
      const ran = on(request.expires_at);
      return ran
        ? `Nobody opened the link before it ran out on ${ran}. You can ask again.`
        : "The link ran out before anybody opened it. You can ask again.";
    }
    case "withdrawn":
      return "You took this one back.";
  }
}
</script>

<template>
  <!-- `data-statsig-block` marks this DOM as never to be recorded: it holds a
       bearer link and the address of a child's parent. Nothing records the
       DOM (there is no session replay), and autocapture drops every click on
       an input, so neither value reaches analytics. -->
  <section class="ask-funding" data-testid="ask-funding" data-statsig-block>
    <p class="ask-label">Ask someone to pay for your seat</p>
    <p class="ask-body">
      We will send them a link. They decide, and they pay — nothing is charged
      to you and nothing happens until they do.
    </p>

    <form class="ask-form" data-testid="ask-form" @submit.prevent="ask">
      <label class="ask-field">
        Their email address
        <input
          v-model="payerEmail"
          type="email"
          autocomplete="off"
          :aria-invalid="error !== null"
          data-testid="ask-email"
        />
      </label>
      <button
        type="submit"
        class="primary ask-submit"
        :disabled="submitting"
        data-testid="ask-submit"
      >
        {{ submitting ? "Sending…" : "Send the request" }}
      </button>
    </form>

    <p v-if="error" class="ask-alert" role="alert" data-testid="ask-error">
      {{ error }}
    </p>

    <template v-if="issued">
      <!-- Both branches, and the link on both. `delivery` decides only what
           we CLAIM to have done: saying "sent" when no mail went out would
           leave a learner waiting on a mailbox nobody wrote to, which is
           the failure rl-757.26 exists to remove. -->
      <p class="ask-sent" role="status" data-testid="ask-sent">
        <template v-if="issued.delivery === 'emailed'">
          Sent to {{ issuedTo }}. Here is the same link, if showing it to them
          yourself is quicker.
        </template>
        <template v-else>
          We could not email {{ issuedTo }}, so nothing has reached them yet.
          Give them this link and it works just the same.
        </template>
      </p>
      <label class="ask-field ask-link">
        The link to your request
        <input
          :value="issued.share_url"
          type="text"
          readonly
          data-testid="ask-share-link"
          @focus="($event.target as HTMLInputElement).select()"
        />
      </label>
      <button
        type="button"
        class="primary"
        data-testid="ask-copy-link"
        @click="copyLink"
      >
        {{ copied ? "Copied" : "Copy the link" }}
      </button>
      <!-- Honest about what the link is. It is not a payment page you can
           post anywhere: it names you to whoever opens it, and whoever
           opens it can answer in your place. -->
      <p class="ask-note" data-testid="ask-link-caution">
        Anyone with this link can see your first name and answer for you, so
        give it straight to the person you are asking and nowhere public. We do
        not keep a copy — if you lose it, ask again and this one stops working.
        <template v-if="on(issued.expires_at)">
          It works until {{ on(issued.expires_at) }}.
        </template>
      </p>
    </template>

    <!-- §11.2.9, and it belongs before the asking rather than after it: a
         learner deciding whether to involve a parent is owed the answer to
         "what will they be able to see?" while it is still their decision. -->
    <p class="ask-note" data-testid="ask-privacy-note">
      They will see that the seat is paid for and in use. They will not see your
      work, your answers or your progress.
    </p>

    <p v-if="notice" class="ask-note" role="status" data-testid="ask-notice">
      {{ notice }}
    </p>

    <template v-if="asks.length > 0">
      <p class="ask-label ask-list-label">Who you have asked</p>
      <ul class="ask-list">
        <li
          v-for="request in asks"
          :key="request.id"
          class="ask-row"
          data-testid="funding-request"
        >
          <p class="ask-who" data-testid="funding-request-email">
            {{ request.payer_email }}
          </p>
          <p class="ask-state" data-testid="funding-request-state">
            {{ askLine(request) }}
          </p>
          <button
            v-if="request.state === 'awaiting'"
            type="button"
            class="secondary"
            :disabled="withdrawing === request.id"
            data-testid="funding-request-withdraw"
            @click="withdraw(request.id)"
          >
            {{ withdrawing === request.id ? "Working…" : "Take this back" }}
          </button>
        </li>
      </ul>
    </template>
  </section>
</template>

<style scoped>
.ask-funding {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.ask-label {
  margin: 0;
  font-size: var(--rl-text-md);
  font-weight: 600;
  color: var(--rl-fg-1);
}

.ask-list-label {
  margin-top: var(--sp-3);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--rl-border-subtle);
}

.ask-body {
  margin: 0;
  font-size: var(--rl-text-base);
  line-height: 1.55;
  color: var(--rl-fg-2);
}

/* Stacked and full width: the learner reading this is on a phone (§4.6). */
.ask-form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.ask-field {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.ask-field input {
  width: 100%;
}

.ask-link input {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-sm);
}

.ask-submit {
  width: 100%;
}

.ask-alert {
  margin: 0;
  font-size: var(--rl-text-base);
  line-height: 1.5;
  color: var(--rl-red-700);
}

.ask-sent {
  margin: 0;
  padding: var(--sp-3);
  background: var(--rl-mint-100);
  border-radius: var(--radius-md);
  font-size: var(--rl-text-base);
  line-height: 1.55;
  color: var(--rl-fg-1);
}

.ask-note {
  margin: 0;
  font-size: var(--rl-text-sm);
  line-height: 1.5;
  color: var(--rl-fg-3);
}

.ask-list {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  margin: 0;
  padding: 0;
  list-style: none;
}

.ask-row {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  align-items: flex-start;
}

.ask-who {
  margin: 0;
  font-size: var(--rl-text-base);
  color: var(--rl-fg-1);
}

.ask-state {
  margin: 0;
  font-size: var(--rl-text-sm);
  line-height: 1.5;
  color: var(--rl-fg-3);
}
</style>
